import { useState } from "react";
import { Palette, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SWATCH_KEYS = ["bg", "barColor", "depArrBarColor", "categoryBgColor", "textColor"];
const newId = () => `sp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

function Swatches({ s }) {
  return (
    <span className="flex">
      {SWATCH_KEYS.map((k) => (
        <span key={k} className="h-4 w-4 -ml-1 first:ml-0 rounded-full border border-white" style={{ background: s?.[k] || "#ccc" }} />
      ))}
    </span>
  );
}

export function StylePresets({ draft, setDraft, persist, saving }) {
  const [name, setName] = useState("");
  const presets = draft.stylePresets || [];

  const save = (next) => {
    setDraft(next);
    return persist(next);
  };

  const saveCurrent = () => {
    const n = name.trim();
    if (!n) return toast.error("Give the look a name, e.g. Diwali");
    const existing = presets.find((p) => p.name.toLowerCase() === n.toLowerCase());
    if (existing && !window.confirm(`Replace the saved look "${existing.name}"?`)) return undefined;
    const preset = { id: existing?.id || newId(), name: n, styleSettings: { ...draft.styleSettings } };
    const list = existing ? presets.map((p) => (p.id === existing.id ? preset : p)) : [...presets, preset];
    setName("");
    return save({ ...draft, stylePresets: list });
  };

  const apply = (p) => save({ ...draft, styleSettings: { ...p.styleSettings } });
  const remove = (p) => {
    if (window.confirm(`Delete the saved look "${p.name}"?`)) save({ ...draft, stylePresets: presets.filter((x) => x.id !== p.id) });
  };

  return (
    <div className="space-y-2 rounded border p-3 bg-slate-50" data-testid="admin-style-presets">
      <Label className="block text-sm font-semibold">
        <Palette size={14} className="inline mr-1" /> Style Presets
      </Label>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Diwali" maxLength={40} data-testid="admin-preset-name" />
        <Button size="sm" onClick={saveCurrent} disabled={saving} data-testid="admin-preset-save">Save current look</Button>
      </div>
      {presets.length === 0 ? (
        <p className="text-[11px] text-muted-foreground" data-testid="admin-preset-empty">No saved looks yet.</p>
      ) : (
        <div className="space-y-1.5" data-testid="admin-preset-list">
          {presets.map((p) => (
            <div key={p.id} className="flex items-center gap-2 rounded border bg-white px-2 py-1.5" data-testid={`admin-preset-${p.id}`}>
              <Swatches s={p.styleSettings} />
              <span className="flex-1 truncate text-sm font-medium">{p.name}</span>
              <Button size="sm" onClick={() => apply(p)} disabled={saving} data-testid={`admin-preset-apply-${p.id}`}>
                <Check size={14} /> Apply
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p)} disabled={saving} data-testid={`admin-preset-delete-${p.id}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">Saves all colours and the background image. Apply switches the live site instantly.</p>
    </div>
  );
}
